import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import debounce from 'lodash/debounce'
import { toHour } from '~/assets/date'

// Provide a single debounce that we can wrap around all fetch calls
const fetchDebounce = debounce((call: () => void) => call(), 300)

/**
 * Global search state.
 */
export const useSearchStore = defineStore('search', () => {
  const cacheStore = useCacheStore()
  const graphqlFetch = useGraphqlFetch()
  const { locale } = useI18n()

  // Search context
  const keyword = ref('')
  const page = ref(1)

  // Search results
  const events = ref<any[]>([])
  const loading = ref(true)
  const pages = ref<any[]>([])
  const series = ref<any[]>([])
  const totalEvents = ref(0)
  const totalPages = ref(0)
  const totalSeries = ref(0)

  // Url parameters
  const urlParameters = computed(() => ({
    keyword: keyword.value || undefined,
    page: page.value === 1 ? undefined : page.value,
  }))

  // Initialize
  function initialize({ keyword: kw, page: pg, search }: { keyword?: any; page?: any; search?: any }) {
    // When there are no values given, i.e. from the URL, we will simply keep the most recently used search context
    if (kw === undefined && pg === undefined && search === undefined) {
      return
    }

    keyword.value = typeof kw === 'string' ? kw : typeof search === 'string' ? search : '' // For backwards compatability we fallback to the parameter 'search' for the keyword
    page.value = typeof pg === 'string' ? Math.max(1, Number.parseInt(pg)) : 1
  }

  // Url parameters
  function setUrlParameters(query: any) {
    const toValue = (value: any, fallback: any) => (value === undefined ? fallback : Array.isArray(value) ? value?.[0] : value)
    // For backwards compatability we fallback to the parameter 'search' for the keyword, same as initialize() does
    keyword.value = toValue(query.keyword, undefined) ?? toValue(query.search, '')
    page.value = Math.max(1, toValue(query.page, 1))
    fetchSearch()
  }

  // Search context
  function reset() {
    // Search context
    keyword.value = ''
    page.value = 1
    // Search results
    events.value = []
    loading.value = true
    pages.value = []
    series.value = []
    totalEvents.value = 0
    totalPages.value = 0
    totalSeries.value = 0
    setTimeout(() => {
      fetchSearch()
    }, 10)
  }
  function setKeyword(newKeyword: string) {
    keyword.value = newKeyword
    page.value = 1
    // Debounce because we do not want to fetch on every keystroke
    fetchDebounce(() => fetchSearch())
  }
  function setPage(newPage: number) {
    page.value = Math.max(1, newPage)
    fetchSearch()
  }

  // Search results
  function setEvents(newEvents: any[]) {
    events.value = newEvents
  }
  function setLoading(newLoading: boolean) {
    loading.value = newLoading
  }
  function setPages(newPages: any[]) {
    pages.value = newPages
  }
  function setSeries(newSeries: any[]) {
    series.value = newSeries
  }
  function setTotalEvents(newTotalEvents: number) {
    totalEvents.value = newTotalEvents
  }
  function setTotalPages(newTotalPages: number) {
    totalPages.value = newTotalPages
  }
  function setTotalSeries(newTotalSeries: number) {
    totalSeries.value = newTotalSeries
  }

  async function fetchSearch() {
    // We actually want to search for events after 'now' and also rounded to the last hour to allow for caching
    const actualDate = toHour('now').toISOString()
    const key = ['search', locale.value, `keyword:${keyword.value}`, `date:${actualDate}`].join(';')
    const ttl = 300

    const fetcher = async () => {
      // Fetch 1024 results, therefore we will not need to fetch new results when we switch pages
      // For the topEvents we just retrieve the first few
      const { searchEvents, searchSeries, searchPages } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query search($keyword: String, $site: [String], $eventEndDate: [String], $importIconcertStatus: [String]) {
        searchEvents: elasticSearch(
          section: "event"
          site: $site
          search: $keyword
          fuzzyness: "${keyword.value.length <= 1 ? '0' : 'AUTO'}"
          eventEndDateRange: $eventEndDate
          orderBy: "eventDate ASC"
          importIconcertStatusFacet: $importIconcertStatus
        ) {
          count
          hits(limit: 3) {
            url
            title
            eventDate
          }
        }

        searchSeries: elasticSearch(section: "series", site: $site, search: $keyword) {
          count
          hits(limit: 3) {
            url
            title
            saleStart: importTixOnlineSaleStart
          }
        }

        searchPages: elasticSearch(
          section: ["page", "campaign", "press", "discovery", "discoveryCollection", "contentOverview"]
          highlight: ["subtitle", "introduction"]
          site: $site
          search: $keyword
        ) {
          count
          hits(limit: 1024) {
            url
            title
            highlights
            introduction
            subtitle
          }
        }
      }`,
        variables: {
          eventEndDate: `gte ${actualDate}`,
          importIconcertStatus: ['1e Optie', 'Bevestigd', 'Contract uit', 'Corona', 'Voor brochure', 'Verplaatst', 'Geannuleerd'],
          keyword: keyword.value,
          site: `${locale.value}Default`,
        },
      })

      return {
        events: searchEvents.hits,
        pages: searchPages.hits,
        series: searchSeries.hits,
        totalEvents: searchEvents.count,
        totalPages: searchPages.count,
        totalSeries: searchSeries.count,
      }
    }

    setLoading(true)
    // Fetch the hits
    const {
      events: fetchedEvents,
      pages: fetchedPages,
      series: fetchedSeries,
      totalEvents: fetchedTotalEvents,
      totalPages: fetchedTotalPages,
      totalSeries: fetchedTotalSeries,
    } = await cacheStore.fetch({
      key,
      ttl,
      fetcher,
      fallback: { events: [], pages: [], series: [], totalEvents: 0, totalPages: 0, totalSeries: 0 },
    })

    setEvents(fetchedEvents)
    setPages(fetchedPages.slice((page.value - 1) * 18, page.value * 18))
    setSeries(fetchedSeries)
    setTotalEvents(fetchedTotalEvents)
    setTotalPages(fetchedTotalPages)
    setTotalSeries(fetchedTotalSeries)
    setLoading(false)
  }

  return {
    keyword,
    page,
    events,
    loading,
    pages,
    series,
    totalEvents,
    totalPages,
    totalSeries,
    urlParameters,
    initialize,
    setUrlParameters,
    reset,
    setKeyword,
    setPage,
    setEvents,
    setLoading,
    setPages,
    setSeries,
    setTotalEvents,
    setTotalPages,
    setTotalSeries,
    fetch: fetchSearch,
  }
})
